import { useRef, useState, type ChangeEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button } from "@lendcord/concord";
import { DocumentCard } from "@/features/profile/components/DocumentCard";
import {
  useConfirmDocumentUploadMutation,
  useRequestDocumentUploadUrlMutation,
  uploadDocumentFile,
} from "@/features/profile/api/profile.api";
import { useGetProfileQuery } from "@/app/profile/api/profile.api";
import { MAX_IMAGE_UPLOAD_SIZE_BYTES, MAX_PDF_UPLOAD_SIZE_BYTES, MIN_DOCUMENT_UPLOAD_SIZE_BYTES } from "@/app/profile/documentUploadLimits";
import { compressImageToLimit } from "@/app/profile/imageCompression";
import type { DocumentType, ProfileDocument } from "@/app/profile/profile.types";

interface DocumentUploadSlotProps {
  type: DocumentType;
  documents: ProfileDocument[];
  label?: string;
  accept?: string;
}

export function DocumentUploadSlot({ type, documents, label, accept = "image/*,.pdf" }: DocumentUploadSlotProps) {
  const intl = useIntl();
  const { refetch } = useGetProfileQuery();
  const [requestDocumentUploadUrl] = useRequestDocumentUploadUrlMutation();
  const [confirmDocumentUpload] = useConfirmDocumentUploadMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  async function handleFileSelected(event: ChangeEvent<HTMLInputElement>) {
    let file = event.target.files?.[0];
    event.target.value = "";
    // The isUploading check guards against a duplicate/re-entrant change
    // event running the whole upload flow twice (observed in testing —
    // React StrictMode's deliberate double-invocation in dev is the likely
    // cause; disabling the trigger button alone doesn't stop this, since it
    // doesn't touch this handler directly).
    if (!file || isUploading) return;

    setUploadError(undefined);

    if (file.size < MIN_DOCUMENT_UPLOAD_SIZE_BYTES) {
      setUploadError(intl.formatMessage({ id: "lender.profile.documentUploadTooSmall" }));
      return;
    }

    const isImage = file.type.startsWith("image/");
    if (isImage && file.size > MAX_IMAGE_UPLOAD_SIZE_BYTES) {
      file = await compressImageToLimit(file, MAX_IMAGE_UPLOAD_SIZE_BYTES);
    }
    const maxBytes = isImage ? MAX_IMAGE_UPLOAD_SIZE_BYTES : MAX_PDF_UPLOAD_SIZE_BYTES;
    if (file.size > maxBytes) {
      setUploadError(intl.formatMessage({ id: "lender.profile.documentUploadTooLarge" }));
      return;
    }

    setIsUploading(true);
    try {
      const { storageKey, uploadUrl } = await requestDocumentUploadUrl(type).unwrap();
      await uploadDocumentFile(uploadUrl, file);
      await confirmDocumentUpload({ type, storageKey }).unwrap();
      await refetch();
    } catch {
      setUploadError(intl.formatMessage({ id: "lender.profile.documentUploadError" }));
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {uploadError && <AlertBanner status="error">{uploadError}</AlertBanner>}

      {documents.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {documents.map((document) => (
            <DocumentCard key={document.id} document={document} onUploadAgain={handleUploadClick} />
          ))}
        </div>
      )}

      <Button
        type="button"
        variant="secondary"
        density="compact"
        onClick={handleUploadClick}
        disabled={isUploading}
        className="self-start"
      >
        {isUploading ? (
          <FormattedMessage id="lender.profile.uploadingButton" />
        ) : label ? (
          label
        ) : (
          <FormattedMessage
            id={documents.length > 0 ? "lender.profile.uploadAgainButton" : "lender.profile.uploadButton"}
          />
        )}
      </Button>

      <input ref={fileInputRef} type="file" onChange={handleFileSelected} accept={accept} className="hidden" />
    </div>
  );
}
